import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { ChartColumn, X } from 'lucide-react'
import type { GridController } from '../../compositor/GridController'
import type {
  SpreadsheetRangeAddress,
  SpreadsheetSparklineAxisMode,
  SpreadsheetSparklineEmptyCellPolicy,
  SpreadsheetSparklineGroup,
  SpreadsheetSparklinePlotBy,
  SpreadsheetSparklineType,
} from '../../protocol/spreadsheetEngineProtocol'
import {
  buildSparklineDialogApplyPlan,
  buildSparklineDialogClearSelectionPlan,
  buildSparklineDialogDeleteGroupPlan,
  buildSparklineDialogGroupLocationsPlan,
  buildSparklineDialogUngroupPlan,
  buildSparklineGroupDraftFromGroup,
  defaultSparklineLocationForSelection,
  filterVisibleSparklineGroups,
  formatSparklineRangeFormula,
  SPARKLINE_AXIS_MODE_OPTIONS,
  SPARKLINE_EMPTY_CELL_POLICY_OPTIONS,
  SPARKLINE_PLOT_BY_OPTIONS,
  SPARKLINE_TYPE_OPTIONS,
  type SparklineDialogApplyPlan,
  type SparklineDialogCommandPlan,
} from '../../spreadsheet/sparklinePresentation'
import './ExcelWorkbookChrome.css'

type ExcelSparklineDialogProps = {
  controller: GridController
  selectedRange: SpreadsheetRangeAddress
  selectionLabel: string
  initialType: SpreadsheetSparklineType
  autoEditExisting?: boolean
  onClose: () => void
  onApplied?: () => void
}

export function ExcelSparklineDialog({
  controller,
  selectedRange,
  selectionLabel,
  initialType,
  autoEditExisting = false,
  onClose,
  onApplied,
}: ExcelSparklineDialogProps) {
  const defaultLocation = useMemo(() => defaultSparklineLocationForSelection(selectedRange), [selectedRange])
  const [type, setType] = useState<SpreadsheetSparklineType>(initialType)
  const [plotBy, setPlotBy] = useState<SpreadsheetSparklinePlotBy>('rows')
  const [dataRangeDraft, setDataRangeDraft] = useState(() => formatSparklineRangeFormula(selectedRange, selectedRange.sheet_id))
  const [locationRangeDraft, setLocationRangeDraft] = useState(() => formatSparklineRangeFormula(defaultLocation, selectedRange.sheet_id))
  const [dateAxis, setDateAxis] = useState(false)
  const [dateRangeDraft, setDateRangeDraft] = useState('')
  const [emptyPolicy, setEmptyPolicy] = useState<SpreadsheetSparklineEmptyCellPolicy>('gap')
  const [displayHidden, setDisplayHidden] = useState(true)
  const [displayXAxis, setDisplayXAxis] = useState(false)
  const [rightToLeft, setRightToLeft] = useState(false)
  const [minAxis, setMinAxis] = useState<SpreadsheetSparklineAxisMode>('individual')
  const [maxAxis, setMaxAxis] = useState<SpreadsheetSparklineAxisMode>('individual')
  const [manualMin, setManualMin] = useState('')
  const [manualMax, setManualMax] = useState('')
  const [lineWeight, setLineWeight] = useState('1.25')
  const [seriesColor, setSeriesColor] = useState('#107c41')
  const [negativeColor, setNegativeColor] = useState('#c2410c')
  const [axisColor, setAxisColor] = useState('#6b7280')
  const [markerColor, setMarkerColor] = useState('#107c41')
  const [markers, setMarkers] = useState(false)
  const [highPoint, setHighPoint] = useState(false)
  const [lowPoint, setLowPoint] = useState(false)
  const [firstPoint, setFirstPoint] = useState(false)
  const [lastPoint, setLastPoint] = useState(false)
  const [negativePoints, setNegativePoints] = useState(true)
  const [editingGroupId, setEditingGroupId] = useState<string | null>(null)
  const [selectedGroupId, setSelectedGroupId] = useState('')
  const [groups, setGroups] = useState<SpreadsheetSparklineGroup[]>([])
  const [groupsLoading, setGroupsLoading] = useState(false)
  const [draftError, setDraftError] = useState<string | null>(null)

  useEffect(() => {
    setType(initialType)
  }, [initialType])

  const refreshGroups = useCallback(async () => {
    const streamer = controller.streamer as unknown as { getSparklineGroups?: () => Promise<{ sparkline_groups?: SpreadsheetSparklineGroup[] }> } | undefined
    if (typeof streamer?.getSparklineGroups !== 'function') return
    setGroupsLoading(true)
    try {
      const payload = await streamer.getSparklineGroups()
      setGroups(Array.isArray(payload.sparkline_groups) ? payload.sparkline_groups : [])
    } catch {
      setGroups([])
    } finally {
      setGroupsLoading(false)
    }
  }, [controller])

  useEffect(() => {
    void refreshGroups()
  }, [refreshGroups])

  const visibleGroups = useMemo(() => filterVisibleSparklineGroups(groups, selectedRange), [groups, selectedRange])

  useEffect(() => {
    if (selectedGroupId && visibleGroups.some((group) => group.id === selectedGroupId)) return
    setSelectedGroupId(visibleGroups[0]?.id ?? '')
  }, [selectedGroupId, visibleGroups])

  const beginEditGroup = useCallback((group: SpreadsheetSparklineGroup) => {
    const draft = buildSparklineGroupDraftFromGroup(group, selectedRange)
    setEditingGroupId(group.id)
    setSelectedGroupId(group.id)
    setType(draft.type)
    setPlotBy(draft.plotBy)
    setDateAxis(draft.dateAxis)
    setEmptyPolicy(draft.emptyPolicy)
    setDisplayHidden(draft.displayHidden)
    setDisplayXAxis(draft.displayXAxis)
    setRightToLeft(draft.rightToLeft)
    setMinAxis(draft.minAxis)
    setMaxAxis(draft.maxAxis)
    setManualMin(draft.manualMin)
    setManualMax(draft.manualMax)
    setLineWeight(draft.lineWeight)
    setMarkers(draft.markers)
    setHighPoint(draft.highPoint)
    setLowPoint(draft.lowPoint)
    setFirstPoint(draft.firstPoint)
    setLastPoint(draft.lastPoint)
    setNegativePoints(draft.negativePoints)
    setSeriesColor(draft.seriesColor)
    setNegativeColor(draft.negativeColor)
    setAxisColor(draft.axisColor)
    setMarkerColor(draft.markerColor)
    setLocationRangeDraft(draft.locationRangeDraft)
    setDataRangeDraft(draft.dataRangeDraft)
    setDateRangeDraft(draft.dateRangeDraft)
    setDraftError(null)
  }, [selectedRange])

  useEffect(() => {
    if (!autoEditExisting || editingGroupId || groupsLoading) return
    const group = visibleGroups.find((entry) => entry.id === selectedGroupId) ?? visibleGroups[0]
    if (group) beginEditGroup(group)
  }, [autoEditExisting, beginEditGroup, editingGroupId, groupsLoading, selectedGroupId, visibleGroups])

  const afterSparklineMutation = () => {
    onApplied?.()
    void refreshGroups()
  }

  const executeApplyPlan = (plan: SparklineDialogApplyPlan): boolean => {
    if (plan.action === 'blocked') {
      setDraftError(plan.error)
      return false
    }
    controller.upsertSparklineGroup(plan.group)
    setEditingGroupId(plan.groupId)
    setDraftError(null)
    return true
  }

  const executeCommandPlan = (plan: SparklineDialogCommandPlan): boolean => {
    switch (plan.action) {
      case 'clear-selection':
        controller.clearSparklinesFromSelection()
        return true
      case 'delete-group':
        controller.deleteSparklineGroup(plan.groupId)
        if (plan.clearEditingGroup) setEditingGroupId(null)
        return true
      case 'group-locations':
        controller.groupSparklineLocations(plan.groupId, plan.locations)
        return true
      case 'ungroup-group':
        controller.ungroupSparklineGroup(plan.groupId)
        if (plan.clearEditingGroup) setEditingGroupId(null)
        return true
      case 'noop':
        return false
    }
  }

  const applySparklineGroup = () => {
    const plan = buildSparklineDialogApplyPlan({
      selectedRange,
      editingGroupId,
      draft: {
        type,
        plotBy,
        dataRangeDraft,
        locationRangeDraft,
        dateAxis,
        dateRangeDraft,
        emptyPolicy,
        displayHidden,
        displayXAxis,
        rightToLeft,
        minAxis,
        maxAxis,
        manualMin,
        manualMax,
        lineWeight,
        seriesColor,
        negativeColor,
        axisColor,
        markerColor,
        markers,
        highPoint,
        lowPoint,
        firstPoint,
        lastPoint,
        negativePoints,
      },
    })
    if (executeApplyPlan(plan)) afterSparklineMutation()
  }

  const clearSelection = () => {
    if (executeCommandPlan(buildSparklineDialogClearSelectionPlan())) afterSparklineMutation()
  }

  const deleteSelectedGroup = () => {
    if (executeCommandPlan(buildSparklineDialogDeleteGroupPlan({ selectedGroupId, editingGroupId }))) {
      afterSparklineMutation()
    }
  }

  const groupSelectedLocations = () => {
    if (executeCommandPlan(buildSparklineDialogGroupLocationsPlan({ selectedGroupId, selectedRange }))) {
      afterSparklineMutation()
    }
  }

  const ungroupSelectedGroup = () => {
    if (executeCommandPlan(buildSparklineDialogUngroupPlan({ selectedGroupId, editingGroupId }))) {
      afterSparklineMutation()
    }
  }

  return (
    <div className="fg-excel-format-dialog-backdrop" role="presentation" onMouseDown={(event) => {
      if (event.target === event.currentTarget) onClose()
    }}>
      <div className="fg-excel-conditional-format-dialog fg-excel-sparkline-dialog" role="dialog" aria-modal="true" aria-label="Sparklines">
        <div className="fg-excel-format-dialog-header">
          <strong className="fg-excel-cell-metadata-title">
            <ChartColumn size={16} />
            Sparklines
          </strong>
          <button type="button" className="fg-excel-icon-button" aria-label="Close" title="Close" onClick={onClose}>
            <X size={16} />
          </button>
        </div>
        <div className="fg-excel-cell-metadata-range">Selection {selectionLabel}</div>
        <div className="fg-excel-format-grid fg-excel-sparkline-grid">
          <label>
            <span>Type</span>
            <select value={type} aria-label="Sparkline type" title="Sparkline type" onChange={(event) => setType(event.target.value as SpreadsheetSparklineType)}>
              {SPARKLINE_TYPE_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>{option.label}</option>
              ))}
            </select>
          </label>
          <label>
            <span>Plot by</span>
            <select value={plotBy} aria-label="Plot by" title="Plot by" onChange={(event) => setPlotBy(event.target.value as SpreadsheetSparklinePlotBy)}>
              {SPARKLINE_PLOT_BY_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>{option.label}</option>
              ))}
            </select>
          </label>
          <label>
            <span>Data range</span>
            <input value={dataRangeDraft} aria-label="Sparkline data range" title="Sparkline data range" onChange={(event) => setDataRangeDraft(event.target.value)} />
          </label>
          <label>
            <span>Location range</span>
            <input value={locationRangeDraft} aria-label="Sparkline location range" title="Sparkline location range" onChange={(event) => setLocationRangeDraft(event.target.value)} />
          </label>
          <label>
            <span>Empty cells</span>
            <select value={emptyPolicy} aria-label="Display empty cells as" title="Display empty cells as" onChange={(event) => setEmptyPolicy(event.target.value as SpreadsheetSparklineEmptyCellPolicy)}>
              {SPARKLINE_EMPTY_CELL_POLICY_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>{option.label}</option>
              ))}
            </select>
          </label>
          <label>
            <span>Min axis</span>
            <select value={minAxis} aria-label="Minimum axis" title="Minimum axis" onChange={(event) => setMinAxis(event.target.value as SpreadsheetSparklineAxisMode)}>
              {SPARKLINE_AXIS_MODE_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>{option.label}</option>
              ))}
            </select>
          </label>
          <label>
            <span>Max axis</span>
            <select value={maxAxis} aria-label="Maximum axis" title="Maximum axis" onChange={(event) => setMaxAxis(event.target.value as SpreadsheetSparklineAxisMode)}>
              {SPARKLINE_AXIS_MODE_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>{option.label}</option>
              ))}
            </select>
          </label>
          <label>
            <span>Manual min</span>
            <input value={manualMin} aria-label="Manual minimum axis value" title="Manual minimum axis value" onChange={(event) => setManualMin(event.target.value)} />
          </label>
          <label>
            <span>Manual max</span>
            <input value={manualMax} aria-label="Manual maximum axis value" title="Manual maximum axis value" onChange={(event) => setManualMax(event.target.value)} />
          </label>
          <label>
            <span>Line weight</span>
            <input value={lineWeight} aria-label="Sparkline line weight" title="Sparkline line weight" onChange={(event) => setLineWeight(event.target.value)} />
          </label>
          <label>
            <span>Series color</span>
            <input type="color" value={seriesColor} aria-label="Sparkline series color" title="Sparkline series color" onChange={(event) => setSeriesColor(event.target.value)} />
          </label>
          <label>
            <span>Negative color</span>
            <input type="color" value={negativeColor} aria-label="Sparkline negative color" title="Sparkline negative color" onChange={(event) => setNegativeColor(event.target.value)} />
          </label>
          <label>
            <span>Axis color</span>
            <input type="color" value={axisColor} aria-label="Sparkline axis color" title="Sparkline axis color" onChange={(event) => setAxisColor(event.target.value)} />
          </label>
          <label>
            <span>Marker color</span>
            <input type="color" value={markerColor} aria-label="Sparkline marker color" title="Sparkline marker color" onChange={(event) => setMarkerColor(event.target.value)} />
          </label>
          <label>
            <span>Date range</span>
            <input value={dateRangeDraft} aria-label="Sparkline date range" title="Sparkline date range" onChange={(event) => setDateRangeDraft(event.target.value)} />
          </label>
          <label className="fg-excel-format-check">
            <input type="checkbox" checked={dateAxis} aria-label="Use date axis" title="Use date axis" onChange={(event) => setDateAxis(event.target.checked)} />
            <span>Date axis</span>
          </label>
          <label className="fg-excel-format-check">
            <input type="checkbox" checked={displayHidden} aria-label="Show hidden data" title="Show hidden data" onChange={(event) => setDisplayHidden(event.target.checked)} />
            <span>Show hidden data</span>
          </label>
          <label className="fg-excel-format-check">
            <input type="checkbox" checked={displayXAxis} aria-label="Show horizontal axis" title="Show horizontal axis" onChange={(event) => setDisplayXAxis(event.target.checked)} />
            <span>Show axis</span>
          </label>
          <label className="fg-excel-format-check">
            <input type="checkbox" checked={rightToLeft} aria-label="Plot right to left" title="Plot right to left" onChange={(event) => setRightToLeft(event.target.checked)} />
            <span>Right to left</span>
          </label>
          <label className="fg-excel-format-check">
            <input type="checkbox" checked={markers} aria-label="Show markers" title="Show markers" onChange={(event) => setMarkers(event.target.checked)} />
            <span>Markers</span>
          </label>
          <label className="fg-excel-format-check">
            <input type="checkbox" checked={negativePoints} aria-label="Show negative points" title="Show negative points" onChange={(event) => setNegativePoints(event.target.checked)} />
            <span>Negative points</span>
          </label>
          <label className="fg-excel-format-check">
            <input type="checkbox" checked={highPoint} aria-label="Show high point" title="Show high point" onChange={(event) => setHighPoint(event.target.checked)} />
            <span>High point</span>
          </label>
          <label className="fg-excel-format-check">
            <input type="checkbox" checked={lowPoint} aria-label="Show low point" title="Show low point" onChange={(event) => setLowPoint(event.target.checked)} />
            <span>Low point</span>
          </label>
          <label className="fg-excel-format-check">
            <input type="checkbox" checked={firstPoint} aria-label="Show first point" title="Show first point" onChange={(event) => setFirstPoint(event.target.checked)} />
            <span>First point</span>
          </label>
          <label className="fg-excel-format-check">
            <input type="checkbox" checked={lastPoint} aria-label="Show last point" title="Show last point" onChange={(event) => setLastPoint(event.target.checked)} />
            <span>Last point</span>
          </label>
        </div>
        {draftError ? <div className="fg-excel-conditional-error">{draftError}</div> : null}
        <div className="fg-excel-conditional-manager fg-excel-sparkline-manager">
          <div className="fg-excel-conditional-manager-header">
            <strong>Groups</strong>
            <button type="button" title="Refresh sparkline groups" onClick={() => void refreshGroups()}>Refresh</button>
          </div>
          {groupsLoading ? (
            <div className="fg-excel-conditional-empty">Loading sparklines...</div>
          ) : visibleGroups.length === 0 ? (
            <div className="fg-excel-conditional-empty">No sparklines in the current selection.</div>
          ) : (
            <div className="fg-excel-sparkline-groups" role="listbox" aria-label="Sparkline groups">
              {visibleGroups.map((group) => (
                <button
                  type="button"
                  key={group.id}
                  className={group.id === selectedGroupId ? 'fg-excel-sparkline-group is-selected' : 'fg-excel-sparkline-group'}
                  title={group.id}
                  aria-selected={group.id === selectedGroupId}
                  onClick={() => setSelectedGroupId(group.id)}
                >
                  <span>{group.type}</span>
                  <span>{group.sparklines.length} location{group.sparklines.length === 1 ? '' : 's'}</span>
                </button>
              ))}
            </div>
          )}
          <div className="fg-excel-sparkline-manager-actions">
            <button type="button" title="Edit selected sparkline group" disabled={!selectedGroupId} onClick={() => {
              const group = visibleGroups.find((entry) => entry.id === selectedGroupId)
              if (group) beginEditGroup(group)
            }}>Edit</button>
            <button type="button" title="Group selected sparkline locations" disabled={!selectedGroupId} onClick={groupSelectedLocations}>Group</button>
            <button type="button" title="Ungroup selected sparkline group" disabled={!selectedGroupId} onClick={ungroupSelectedGroup}>Ungroup</button>
            <button type="button" title="Delete selected sparkline group" disabled={!selectedGroupId} onClick={deleteSelectedGroup}>Delete</button>
          </div>
        </div>
        <div className="fg-excel-format-footer">
          <button type="button" title="Clear sparklines from selection" onClick={clearSelection}>Clear</button>
          <button type="button" title="Cancel" onClick={onClose}>Cancel</button>
          <button type="button" title="Apply sparkline settings" className="fg-excel-cell-metadata-button-primary" onClick={applySparklineGroup}>{editingGroupId ? 'Save' : 'OK'}</button>
        </div>
      </div>
    </div>
  )
}
